import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { ColorMap } from "@/lib/shaders/shared-color";
import { BuildFileControls } from "@/lib/schemas/geometry.schemas";
import { PathSettings } from "../hooks/3d/use-get-path-settings";

const DEFAULT_PATH_STATS: PathSettings = {
  pointRange: [0, 0],
  layerRange: [0, 0],
  availableKeys: [],
  availableTypes: [],
  scalarRanges: {},
  integerScalars: [],
  availableTraces: [],
  typeIntegerMapping: {},
};

const DEFAULT_CONTROLS: BuildFileControls = {
  colorMap: ColorMap.JET,
  layerMin: 0.0,
  layerMax: Number.POSITIVE_INFINITY,
  pointMin: 0.0,
  pointMax: Number.POSITIVE_INFINITY,
  traceConfigs: {
    contact: { show: true, showPoints: false, showLines: true, showQuivers: false },
    r1_tip: { show: true, showPoints: false, showLines: true, showQuivers: false },
    r2_tip: { show: true, showPoints: false, showLines: true, showQuivers: false },
  },
  scalarDataSource: "Default",
  typesToVisualize: [],
  showStartPts: true,
  scalarMin: Number.NEGATIVE_INFINITY,
  scalarMax: Number.POSITIVE_INFINITY,
};

export type BuildFileStatus = "idle" | "loading" | "loaded" | "error";

export const useBuildFileStore = create<{
  controls: BuildFileControls;
  pathStats: PathSettings;
  status: BuildFileStatus;
  errorMessage: string | null;
  setControls: (controls: BuildFileControls) => void;
  updateControl: <K extends keyof BuildFileControls>(key: K, value: BuildFileControls[K]) => void;
  setPathStats: (statistics: PathSettings) => void;
  clearPathStats: () => void;
  updatePathStats: <K extends keyof PathSettings>(key: K, value: PathSettings[K]) => void;
  setStatus: (status: BuildFileStatus) => void;
  setErrorMessage: (errorMessage: string | null) => void;
  resetRangeControls: () => void;
  reset: () => void;
}>()(
  devtools(
    persist(
      (set) => ({
        controls: DEFAULT_CONTROLS,
        pathStats: DEFAULT_PATH_STATS,
        status: "idle" as BuildFileStatus,
        errorMessage: null,
        setControls: (controls) => set({ controls }),
        updateControl: (key, value) =>
          set((state) => ({
            controls: { ...state.controls, [key]: value },
          })),
        setPathStats: (statistics) => set({ pathStats: statistics }),
        clearPathStats: () => set({ pathStats: DEFAULT_PATH_STATS }),
        updatePathStats: (key, value) =>
          set((state) => ({
            pathStats: { ...state.pathStats, [key]: value },
          })),
        setStatus: (status) => set({ status }),
        setErrorMessage: (errorMessage) => set({ errorMessage }),
        resetRangeControls: () =>
          set((state) => ({
            controls: {
              ...state.controls,
              layerMin: DEFAULT_CONTROLS.layerMin,
              layerMax: DEFAULT_CONTROLS.layerMax,
              pointMin: DEFAULT_CONTROLS.pointMin,
              pointMax: DEFAULT_CONTROLS.pointMax,
              scalarMin: DEFAULT_CONTROLS.scalarMin,
              scalarMax: DEFAULT_CONTROLS.scalarMax,
            },
          })),
        reset: () =>
          set({
            controls: DEFAULT_CONTROLS,
            pathStats: DEFAULT_PATH_STATS,
            status: "idle",
            errorMessage: null,
          }),
      }),
      {
        name: "buildfile-controls",
        storage: createJSONStorage(() =>
          process.env.VITEST
            ? { getItem: () => null, setItem: () => {}, removeItem: () => {} }
            : localStorage
        ),
        // pathStats is derived from whichever path is loaded, so it is recomputed, never restored.
        partialize: (state) => ({ controls: state.controls }),
      }
    ),
    { name: "BuildFileStore", trace: process.env.NODE_ENV === "development" }
  )
);
